import { useCallback, useRef } from "react";
import { API_BASE } from "../lib/api";

/**
 * Streaming SSE client for Stella /api/chats/{id}/send.
 * We use fetch + ReadableStream so we can send POST body + abort mid-stream.
 * Events: "delta", "status", "review_path", "done", "error"
 */
export default function useStreamingChat() {
  const controllerRef = useRef(null);

  const stop = useCallback(() => {
    if (controllerRef.current) {
      try { controllerRef.current.abort(); } catch (_) {}
      controllerRef.current = null;
    }
  }, []);

  const send = useCallback(async ({ chatId, body, handlers }) => {
    stop();
    const ctrl = new AbortController();
    controllerRef.current = ctrl;

    let response;
    try {
      response = await fetch(`${API_BASE}/chats/${chatId}/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
    } catch (err) {
      if (err.name === "AbortError") { handlers?.onAbort?.(); return; }
      handlers?.onError?.({ code: "network", message: "Network error" });
      return;
    }

    if (!response.ok) {
      let detail = "Request failed";
      try {
        const j = await response.json();
        detail = j.detail || detail;
      } catch (_) {}
      handlers?.onError?.({ code: response.status, message: detail });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const evt = parseSseBlock(raw);
          if (!evt) continue;
          switch (evt.event) {
            case "delta": handlers?.onDelta?.(evt.data.content || ""); break;
            case "status": handlers?.onStatus?.(evt.data); break;
            case "review_path": handlers?.onReviewPath?.(evt.data); break;
            case "chip": handlers?.onChip?.(evt.data); break;
            case "done": handlers?.onDone?.(evt.data); break;
            case "error": handlers?.onError?.(evt.data); break;
            case "brain_status": handlers?.onBrainStatus?.(evt.data); break;
            case "brain_agents": handlers?.onBrainAgents?.(evt.data); break;
            case "brain_review": handlers?.onBrainReview?.(evt.data); break;
            case "brain_bypass": handlers?.onBrainBypass?.(evt.data); break;
            case "brain_done": handlers?.onBrainDone?.(evt.data); break;
            default: break;
          }
        }
      }
    } catch (err) {
      if (err.name === "AbortError") { handlers?.onAbort?.(); return; }
      handlers?.onError?.({ code: "stream", message: "Stream error" });
    } finally {
      controllerRef.current = null;
    }
  }, [stop]);

  return { send, stop };
}

function parseSseBlock(block) {
  let event = "message";
  const dataLines = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  const data = dataLines.join("\n");
  try {
    return { event, data: data ? JSON.parse(data) : {} };
  } catch {
    return null;
  }
}
